import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useGetAllProcesses } from "@/hooks";
import { useTriggerRunList } from "@/hooks/process-trigger/use-process-trigger";
import RunStatusBadge from "@/components/process-trigger/run-status-badge";
import TriggerRunActions from "@/components/process-trigger/trigger-run-actions";
import {
  managementApplicationPath,
  type TriggerRunStatus,
} from "@/types/process-trigger.types";

const STATUSES: TriggerRunStatus[] = [
  "AWAITING_USER",
  "STARTED",
  "DECLINED",
  "EXPIRED",
  "CANCELLED",
  "SKIPPED",
];
const ALL = "__all__";

const TriggeredProcessesPage: React.FC = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<string>("AWAITING_USER");
  const [processId, setProcessId] = useState<string>(ALL);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const { data: processes } = useGetAllProcesses();

  // Debounce the search box so every keystroke isn't a request.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const params = useMemo(
    () => ({
      page,
      limit: 20,
      status: status === ALL ? undefined : (status as TriggerRunStatus),
      processId: processId === ALL ? undefined : processId,
      search: search || undefined,
    }),
    [page, status, processId, search]
  );
  const { data, isLoading, isError } = useTriggerRunList(params);
  const runs = data?.data ?? [];
  const meta = data?.meta;

  const personName = (u: (typeof runs)[number]["targetUser"]) =>
    u ? `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || u.email : "—";

  return (
    <div className="p-4 lg:p-6 space-y-4">
      <div>
        <h1 className="text-xl font-medium text-darkBlue">{t("processTriggers.runs.pageTitle")}</h1>
        <p className="text-sm text-gray-500">{t("processTriggers.runs.pageDescription")}</p>
      </div>

      <div className="flex flex-col md:flex-row gap-3">
        <Input
          className="md:max-w-xs"
          value={searchInput}
          placeholder={t("processTriggers.runs.search")}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <Select
          value={status}
          onValueChange={(value) => {
            setStatus(value);
            setPage(1);
          }}
        >
          <SelectTrigger className="md:w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t("processTriggers.runs.allStatuses")}</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {t(`processTriggers.status.${s}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={processId}
          onValueChange={(value) => {
            setProcessId(value);
            setPage(1);
          }}
        >
          <SelectTrigger className="md:w-64">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t("processTriggers.runs.allSourceProcesses")}</SelectItem>
            {(processes?.data ?? []).map((p: any) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left text-gray-600">
              <th className="p-3 font-medium">{t("processTriggers.runs.process")}</th>
              <th className="p-3 font-medium">{t("processTriggers.runs.startedBy")}</th>
              <th className="p-3 font-medium">{t("processTriggers.runs.recipient")}</th>
              <th className="p-3 font-medium">{t("processTriggers.runs.status")}</th>
              <th className="p-3 font-medium">{t("processTriggers.runs.date")}</th>
              <th className="p-3 font-medium" />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="p-6 text-center text-gray-500">
                  {t("processManagement.loading")}
                </td>
              </tr>
            ) : isError ? (
              <tr>
                <td colSpan={6} className="p-6 text-center text-red-600">
                  {t("processTriggers.runs.loadFailed")}
                </td>
              </tr>
            ) : runs.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-6 text-center text-gray-500">
                  {t("processTriggers.runs.empty")}
                </td>
              </tr>
            ) : (
              runs.map((run) => {
                const overdue =
                  run.status === "AWAITING_USER" && !!run.dueAt && dayjs(run.dueAt).isBefore(dayjs());
                return (
                  <tr key={run.id} className="border-t align-top">
                    <td className="p-3">
                      {run.childApplicantProcess ? (
                        <Link
                          className="text-darkBlue underline"
                          to={managementApplicationPath(
                            "admin",
                            run.childApplicantProcess.applicationType,
                            run.targetProcess.id,
                            run.childApplicantProcess.id
                          )}
                        >
                          {run.targetProcess.name}
                        </Link>
                      ) : (
                        run.targetProcess.name
                      )}
                      {run.trigger && (
                        <p className="text-xs text-gray-500">{run.trigger.name}</p>
                      )}
                    </td>
                    <td className="p-3">
                      <Link
                        className="text-darkBlue underline"
                        to={managementApplicationPath(
                          "admin",
                          run.parentApplicantProcess.applicationType,
                          run.parentApplicantProcess.processId,
                          run.parentApplicantProcess.id
                        )}
                      >
                        {run.parentApplicantProcess.process.name}
                      </Link>
                    </td>
                    <td className="p-3">
                      {personName(run.targetUser)}
                      {run.targetUser && (
                        <p className="text-xs text-gray-500">{run.targetUser.email}</p>
                      )}
                    </td>
                    <td className="p-3 space-y-1">
                      <RunStatusBadge status={run.status} overdue={overdue} />
                      {run.statusReason && (
                        <p className="text-xs text-gray-500">{run.statusReason}</p>
                      )}
                    </td>
                    <td className="p-3 text-gray-600 whitespace-nowrap">
                      {dayjs(run.createdAt).format("MMM D, YYYY")}
                      {run.dueAt && (
                        <p className="text-xs text-gray-500">
                          {t("processTriggers.todo.dueOn", {
                            date: dayjs(run.dueAt).format("MMM D, YYYY"),
                          })}
                        </p>
                      )}
                    </td>
                    <td className="p-3">
                      <TriggerRunActions run={run} />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {meta && meta.totalPages > 1 && (
        <div className="flex items-center justify-end gap-3 text-sm">
          <button
            type="button"
            className="px-3 py-1 border rounded disabled:opacity-40"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            {t("processTriggers.runs.previous")}
          </button>
          <span>
            {page} / {meta.totalPages}
          </span>
          <button
            type="button"
            className="px-3 py-1 border rounded disabled:opacity-40"
            disabled={page >= meta.totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            {t("processTriggers.runs.next")}
          </button>
        </div>
      )}
    </div>
  );
};

export default TriggeredProcessesPage;
